import React, { useRef, useState, useMemo, useEffect } from 'react';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { NavigationContext, useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import {
  FlatList,
  Image,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useAuth } from '../context/AuthContext';
import { useStoryProgress } from '../context/StoryProgressContext';
import { apiFetch, ApiError } from '../api/client';
import { getScenarioForMission, loadRuntimeScenario } from '../data/scenarios/index';
import { abandonMissionRun, fetchMissionRun } from '../api/missionRun';
import { fetchMissionTimers, startItemTimer, chronoAAfficher, type MissionTimer } from '../api/missionTimers';
import MissionCountdown from '../components/MissionCountdown';
import { useMissionLaunch } from '../hooks/useMissionLaunch';
import { useMissionMusic, resetDucking } from '../hooks/useMissionMusic';
import { type Palette } from '../utils/cyb';
import { usePalette } from '../hooks/usePalette';
import { useResponsive } from '../hooks/useResponsive';
import { useMissions } from '../context/MissionsContext';
import { usePlayer } from '../context/PlayerContext';
import RemoteImage from '../components/RemoteImage';
import ActionLoader from '../components/ActionLoader';
import { azuriteFallback } from '../config/env';
import SceneReaderScreen from './SceneReaderScreen';
import MissionChatScreen from './MissionChatScreen';
import UsChatScreen from './UsChatScreen';
import CharacterChatScreen from './CharacterChatScreen';
import TaskDetailScreen from './TaskDetailScreen';
import QuizScreen from './QuizScreen';
import MissionCardsScreen from './MissionCardsScreen';
import { buildNavSequence } from '../utils/itemNav';
import { sequenceStatus } from '../utils/sequenceStatus';
import { numParamOr } from '../utils/routeParams';
import { useT } from '../i18n/useT';
// CCT-346 — the three screens that open OVER a mission instead of taking the player out of it.
import ChatScreen from './ChatScreen';
import DashboardScreen from './DashboardScreen';
import NotificationsScreen from './NotificationsScreen';
import { MissionPanel, registerMissionPanels } from '../utils/missionPanel';


const TYPE_COLORS: Record<string, { bg: string; text: string; label: string }> = {
  scene:    { bg: 'rgba(0,240,255,0.12)',   text: '#00f0ff', label: 'SCENE' },
  chat:     { bg: 'rgba(255,122,0,0.12)',   text: '#ff7a00', label: 'CHAT' },
  task:     { bg: 'rgba(0,230,118,0.12)',   text: '#00e676', label: 'TASK' },
  quiz:     { bg: 'rgba(168,85,247,0.12)',  text: '#a855f7', label: 'QUIZ' },
  card:     { bg: 'rgba(255,122,0,0.12)',   text: '#ff7a00', label: 'CARD' },
  incident: { bg: 'rgba(255,45,85,0.12)',   text: '#ff2d55', label: 'INCIDENT' },
};

/** Nature d'un élément, en une icône — pour la colonne PROGRESSION, où les titres sont
 *  tronqués et où rien ne disait ce qu'on s'apprêtait à ouvrir.
 *
 *  ⚠️ UNE ICÔNE, PAS UNE COULEUR. `chat` et `card` partagent le même orange dans
 *  `TYPE_COLORS` : une pastille colorée seule les rendrait indiscernables, ce qui est
 *  précisément la distinction qu'on veut donner. */
const TYPE_ICONE: Record<string, string> = {
  scene: 'film-outline',
  story: 'film-outline',
  chat: 'chatbubbles-outline',
  card: 'document-text-outline',
  quiz: 'help-circle-outline',
  task: 'checkbox-outline',
  incident: 'warning-outline',
};

interface Objective { id: number; title: string; completed: boolean; }

interface SequenceItem {
  id: number;
  /** Original string id from JSON (e.g. `scene1_chat`) for downstream screen lookup. */
  sourceId?: string;
  type: 'scene' | 'chat' | 'card' | 'quiz' | 'task';
  title: string;
  subtitle?: string;
  description?: string;
  estimatedDuration: string;
  /** Barrière BLOQUANTE (authoré) : tant qu'elle n'est pas complétée, TOUT ce qui la suit
   *  est verrouillé. Un élément non bloquant ne verrouille PAS ses successeurs (miroir du
   *  gating serveur `_gate_sequence`). */
  blocking?: boolean;
  backendLocked?: boolean;
  status: 'locked' | 'available' | 'completed';
  image?: any;
  sceneNumber?: number;
  priority?: 'Low' | 'Medium' | 'High' | 'Critical';
  characterAvatar?: any;
  characters?: string[];
}

interface ChapterGroup {
  id: number;            // numéro de chapitre (1-based) DE LA MISSION
  title: string;
  isCurrent: boolean;    // le chapitre affiché à l'écran
  // `locked` tel que LIVRÉ par le backend (autorité sur le contenu réellement téléchargé :
  // un chapitre verrouillé n'a que le SQUELETTE de sa séquence). undefined => pas d'info
  // backend, on retombe sur l'heuristique locale (chapitre précédent 100% terminé).
  backendLocked?: boolean;
  // ⚠️ `blocking` FAIT PARTIE DU GROUPE, sinon le repli local ne peut pas appliquer la même
  // règle que le serveur : c'est l'AUTEUR qui choisit ce qui verrouille, et sans ce champ
  // l'app retomberait sur « tous les items », donc sur un verrou que le serveur ignore.
  items: { id: string; type: string; title: string; blocking?: boolean; backendLocked?: boolean }[];
}

interface ChapterDetail {
  id: number; title: string; subtitle: string;
  progress: number; estimatedTime: number; isUnlocked: boolean; isCompleted: boolean;
  objectives: Objective[]; sequence: SequenceItem[];
}

interface ChatMessage { from: 'player' | 'char'; text: string; }

const storyKeyMap: Record<number, string> = {
  1: 'egocrisis', 101: 'egocrisis',
  2: 'foundation_gamble',
  102: 'architecture_under_fire',
  201: 'architecture_under_fire',
  3: 'data_storm',
  109: 'cloud_heist', 110: 'neural_override', 111: 'pipeline_inferno',
  202: 'quantum_gambit', 203: 'merge_conflict',
  108: 'arabic_modern', 112: 'arabic_modern', 113: 'arabic_modern',
  114: 'arabic_modern', 115: 'arabic_modern',
};

const MISSION_BANNERS: Record<string, any> = {
  foundation_gamble: require('../../assets/images/missions/foundation_gamble.png'),
  architecture_under_fire: require('../../assets/images/missions/architecture_under_fire.png'),
  cloud_heist:       require('../../assets/images/missions/cloud_heist_banner.png'),
  neural_override:   require('../../assets/images/missions/neural_override_banner.png'),
  pipeline_inferno:  require('../../assets/images/missions/pipeline_inferno_banner.png'),
  data_storm:        require('../../assets/images/missions/data_storm.png'),
};
const FALLBACK_BANNER = require('../../assets/images/missions/architecture_under_fire.png');

const PRIORITY_COLORS: Record<string, string> = {
  Low: '#10B981', Medium: '#3B82F6', High: '#F97316', Critical: '#EF4444',
};

// Avatar d'un personnage : le SCÉNARIO (base, via /me/stories/{id}/scenario) fait foi — il
// porte une `avatarUrl` publique par personnage.
//
// ⚠️ PLUS AUCUN REPLI SUR LE BUNDLE. Il servait « les histoires historiques pas encore en
// base » — mais toutes le sont, et le repli montrait alors un visage qui ne venait pas de la
// mission jouée. Sans avatar en base, on rend `null` : l'écran affiche l'initiale colorée,
// ce qui DIT qu'il manque une photo au lieu d'en inventer une.
function getCharacterAvatar(_storyKey: string, characterName: string, scenarioChars?: any[]): any {
  const fromDb = (scenarioChars ?? []).find((c: any) => c.name === characterName);
  return fromDb?.avatarUrl ? { uri: fromDb.avatarUrl } : null;
}

/** Combien de visages tiennent avant que la rangée ne déborde de la carte. */
const AVATARS_VISIBLES = 4;

function SceneAvatarRow({ characters, storyKey, scenarioChars }: { characters: string[]; storyKey: string; scenarioChars?: any[] }) {
  const C = usePalette();
  const avS = useMemo(() => makeAvatarStyles(C), [C]);
  // ⚠️ LA TRONCATURE EST UN FAIT D'AFFICHAGE, PAS DE DONNÉE. Elle vivait dans la construction
  // de la séquence (`.slice(0, 3)`) : les participants au-delà du 3e étaient PERDUS avant
  // d'arriver ici, et rien ne disait qu'il en manquait — une discussion à cinq paraissait
  // n'en compter que trois. On les garde tous et on compte le reste.
  const visibles = characters.slice(0, AVATARS_VISIBLES);
  const reste = characters.length - visibles.length;
  return (
    <View style={avS.row}>
      {visibles.map((name, idx) => {
        const avatar = getCharacterAvatar(storyKey, name, scenarioChars);
        return (
          <View key={name} style={[avS.circle, idx > 0 && avS.overlap]}>
            {avatar
              ? <Image source={avatar} style={avS.img} resizeMode={"cover"} />
              : <View style={avS.fallback}><Text style={avS.init}>{name[0]}</Text></View>}
          </View>
        );
      })}
      {reste > 0 && (
        <View
          style={[avS.circle, avS.overlap, avS.reste]}
          // Les noms manquants restent lisibles au survol : le compteur dit COMBIEN,
          // l'infobulle dit QUI. Sans elle, « +2 » n'apprend rien.
          {...({ title: characters.slice(AVATARS_VISIBLES).join(', ') } as any)}
        >
          <Text style={avS.overflowText}>+{reste}</Text>
        </View>
      )}
    </View>
  );
}
const makeAvatarStyles = (C: Palette) => StyleSheet.create({
  row: { flexDirection: 'row', alignItems: 'center' },
  circle: { width: 34, height: 34, borderRadius: 17, overflow: 'hidden', borderWidth: 2, borderColor: C.bg },
  overlap: { marginLeft: -10 },
  img: { width: 34, height: 34 },
  fallback: { width: 34, height: 34, backgroundColor: '#6366F1', alignItems: 'center', justifyContent: 'center' },
  init: { color: '#fff', fontSize: 12, fontWeight: '700' },
  reste: { backgroundColor: C.surface, alignItems: 'center', justifyContent: 'center' },
  overflowText: { color: C.textSec, fontSize: 11, fontWeight: '700' },
});

export default function ChapterDetailScreen() {
  const t = useT();
  // ⚠️ LA CHARTE SUIT LE THÈME. Elle était importée en dur (`CYB`, dark) : la mission
  // restait dark alors que l'application entière passait en clair.
  const C = usePalette();
  const s = useMemo(() => makeStyles(C), [C]);
  const navigation = useNavigation<any>();
  const route = useRoute<any>();

  // CCT-252 — TOUJOURS des nombres. Par URL (`/chapter/?missionId=3`) ces params arrivent en
  // CHAÎNES ; tout `===` contre un id numérique bascule alors en silence (cf. utils/routeParams).
  const chapterId = numParamOr(route.params?.chapterId, 1);
  const missionId = numParamOr(route.params?.missionId, 1);

  // Progression DB-driven (items complétés), partagée + réactive.
  const { getCompletedItems, loadProgress } = useStoryProgress();
  const itemKey = (it: SequenceItem): string => (it.sourceId ?? String(it.id));
  const completedItems = getCompletedItems(missionId);

  // --- Auth + passerelle de déblocage / chargement du scénario blob -------
  // storyChapterId fourni par ChaptersScreen (ex. "chapter-1"). Connecté : on
  // charge le scénario depuis le BLOB et on vérifie le déblocage (403 -> verrou).
  const { user, getAccessToken } = useAuth();
  const storyChapterId: string | null = route.params?.storyChapterId ?? null;
  // ⚠️ `not_started` : LA NAVIGATION DIRECTE DOIT LE RECONNAÎTRE. Une URL collée ou un F5 sur
  // `/chapter/?missionId=1&chapterId=1` contourne l'écran de lancement : la partie n'a alors
  // aucune langue figée, le moteur n'a jamais été demandé, et l'écran affichait quand même le
  // chapitre — dans la langue d'origine, avec un moteur qui ne connaît aucune user story.
  // Mesuré sur la mission 1 : `language` NULL et `status` « not_started » sur un écran qui
  // annonçait 12/12. Mieux vaut refuser de rendre et DIRE pourquoi qu'afficher une mission
  // crédible qui ne mène à rien.
  const [gate, setGate] = useState<'ok' | 'loading' | 'locked' | 'not_started' | 'error'>(user ? 'loading' : 'ok');
  /** `false` = le joueur n'a pas de partie du tout (ni essai, ni achat) : rien à démarrer ici. */
  const [enrolled, setEnrolled] = useState(true);
  /** Incrémenté après un démarrage réussi, pour rejouer la passerelle. */
  const [startTick, setStartTick] = useState(0);
  const [abandonAsk, setAbandonAsk] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const [abandonFailed, setAbandonFailed] = useState(false);
  const { launch, dialog: launchDialog } = useMissionLaunch();
  // Incrémenté après chargement du scénario blob -> force le recalcul de `scenario`.
  const [scenarioTick, setScenarioTick] = useState(0);
  const completePostedRef = useRef(false);

  // Scénario : blob (store runtime) en priorité, repli local ; recalculé au tick.
  const scenario = useMemo(() => getScenarioForMission(missionId), [missionId, scenarioTick]);
  // CCT-119 — une notification désigne le chapitre par son ID (« ch-518 »), pas par son rang.
  // Sans cette résolution, `chapterId` (un INDEX 1-based) restait à sa valeur par défaut et le
  // joueur atterrissait sur le chapitre 1 quel que soit le chapitre annoncé.
  const chapterIdxFromKey = storyChapterId
    ? (scenario?.chapters ?? []).findIndex((c: any) => String(c.id) === String(storyChapterId))
    : -1;
  // CCT-126 — un deep-link d'ÉLÉMENT (`openItemId`, ex. l'US d'une relance) ne dit pas dans quel
  // chapitre il vit. On cherche donc l'élément dans TOUS les chapters : sinon il était cherché
  // dans le seul chapitre courant (le n°1 par défaut), introuvable, et l'écran restait sur la
  // timeline — ou pire, `openFirstPending` ouvrait le 1er élément venu (une scène) à sa place.
  const deepItemId: string | null = (route.params?.usElementId ?? route.params?.openItemId ?? null);
  const chapterIdxFromItem = deepItemId
    ? (scenario?.chapters ?? []).findIndex((c: any) =>
        (c.sequence ?? []).some((it: any) => String(it.id) === String(deepItemId)))
    : -1;
  const chapterIdx = chapterIdxFromItem >= 0 ? chapterIdxFromItem
    : chapterIdxFromKey >= 0 ? chapterIdxFromKey : chapterId - 1;
  const scenarioChapter = scenario?.chapters?.find((_c: any, i: number) => i === chapterIdx) ?? scenario?.chapters?.[0];
  const storyKey = storyKeyMap[missionId] ?? 'egocrisis';

  const { isDesktop } = useResponsive();
  const { activeMissions, trialMissions, allMarketplaceMissions } = useMissions();
  const { playerName, playerAvatar } = usePlayer();

  // CCT-130 — piste d'ambiance : la plus SPÉCIFIQUE gagne, élément > chapitre > mission.
  // `activeItem` est déclaré plus bas ; on lit donc l'id d'élément depuis l'état à ce
  // moment-là via une ref stable (le hook audio ne se relance que si l'URL change).
  const missionMusic: string | null = (() => {
    const m: any = activeMissions.find((x: any) => x.id === missionId)
      ?? trialMissions.find((x: any) => x.id === missionId)
      ?? allMarketplaceMissions.find((x: any) => x.id === missionId);
    return (scenario as any)?.music ?? m?.music ?? null;
  })();

  useEffect(() => {
    const token = getAccessToken();
    if (!user || !token) { setGate('ok'); return; }
    let cancelled = false;
    setGate('loading');
    (async () => {
      try {
        // 0) LA PARTIE EST-ELLE LANCÉE ? Avant tout le reste : charger le scénario et réveiller
        // le moteur pour une partie jamais démarrée, c'est produire un écran qui ment.
        // ⚠️ UN SERVEUR MUET NE BLOQUE PAS. `fetchMissionRun` rend `null` sur échec réseau :
        // on continue alors comme avant, plutôt que de barrer l'entrée sur une incertitude.
        const runState = await fetchMissionRun(token, missionId);
        if (cancelled) return;
        // ⚠️ ON LIT `started`, PAS LA LANGUE. Le premier garde testait `language === null` et
        // laissait passer la mission 1 : langue figée sur `en`, mais `status` toujours
        // « not_started » — parce que `start_run` ne posait que la langue, jamais le statut.
        // Une garde qui redéduit une règle du serveur finit par en appliquer une autre.
        if (!runState) throw new Error('Run state unavailable');
        if (runState && (!runState.enrolled || runState.started === false)) {
          setEnrolled(!!runState.enrolled);
          setGate('not_started');
          return;
        }
        // 1) Charge le scénario depuis le BLOB (force refetch -> contenu/déblocage à jour).
        await loadRuntimeScenario(
          missionId,
          () => apiFetch(`/me/stories/${missionId}/scenario`, { token, auth: true }),
          true,
        );
        if (cancelled) return;
        setScenarioTick(t => t + 1);
        // 1b) Charge la progression (items complétés) depuis la base.
        void loadProgress(missionId);
        // 1c) Réveille le MissionEngine (CCT-65) : rappels selon la progression. Best-effort.
        void apiFetch(`/me/stories/${missionId}/engine/tick`, { method: 'POST', token, auth: true }).catch(() => {});
        // 2) Si on vient des Chapitres (storyChapterId), vérifie le déblocage (403 -> verrou).
        if (storyChapterId) {
          try {
            await apiFetch(`/me/stories/${missionId}/chapters/${storyChapterId}`, { token, auth: true });
            if (!cancelled) { setGate('ok'); }
          } catch (e: any) {
            if (!cancelled) { setGate(e instanceof ApiError && e.status === 403 ? 'locked' : 'error'); }
          }
        } else if (!cancelled) {
          setGate('ok');
        }
      } catch {
        if (!cancelled) setGate('error');
      }
    })();
    return () => { cancelled = true; };
  }, [storyChapterId, missionId, user, getAccessToken, loadProgress, startTick]);

  const timelineScrollRef = useRef<ScrollView>(null);

  // ⚠️ CHRONOMÈTRES (CCT-288) — CHARGÉS, JAMAIS CALCULÉS ICI. L'app ne connaît que des
  // ÉCHÉANCES émises par le serveur ; c'est lui qui décide de l'expiration, et il la revérifie
  // à la validation. Un décompte tenu par le client serait tenu par qui contrôle le client.
  const [timers, setTimers] = useState<MissionTimer[]>([]);
  const chargerTimers = React.useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) return;
    setTimers(await fetchMissionTimers(token, missionId));
  }, [user, missionId]);
  const itemPositions = useRef<number[]>([]);


  const [teamChat, setTeamChat] = useState<{ characterId: number | null; name: string } | null>(null);
  const [mobileTab, setMobileTab] = useState<'timeline' | 'team' | 'progression'>('timeline');
  const [activeItem, setActiveItem] = useState<{ type: string; params: any } | null>(null);
  const refreshAfterDialogue = React.useCallback(async () => {
    // The chat has already refreshed the shared scenario, including newly unlocked content.
    setScenarioTick(t => t + 1);
  }, []);
  useFocusEffect(React.useCallback(() => {
    // A mobile chat is a separate screen; returning must read its refreshed scenario too.
    setScenarioTick(t => t + 1);
  }, []));

  // CCT-346 — notifications, conversations and board open OVER the mission.
  // ⚠️ SUBSCRIBED ON FOCUS, NOT ON MOUNTING: this screen STAYS MOUNTED in the stack once the
  // mission is left (that is exactly why the music follows focus, CCT-146). Registered on
  // mounting, it would keep answering from the mission list — a click opening a panel over an
  // invisible screen, that is to say a click that appears to do nothing.
  const [missionPanel, setMissionPanel] = useState<MissionPanel | null>(null);
  useFocusEffect(React.useCallback(() => {
    const off = registerMissionPanels(setMissionPanel);
    // Leaving the mission closes the panel: reopening it later on a state from two screens ago
    // would be worse than reopening it empty.
    return () => { off(); setMissionPanel(null); };
  }, []));

  // The navigation handed to the panel's content. `useNavigation()` reads it from the context, so
  // the three screens keep working untouched — they simply come back here instead of leaving.
  const panelNav = useMemo(() => ({
    ...(navigation as any),
    navigate: (name: any, params?: any) => { setMissionPanel(null); (navigation as any).navigate(name, params); },
    goBack: () => setMissionPanel(null),
  }), [navigation]);

  // CCT-166 — chapitre RÉELLEMENT ouvert : quand on navigue d'élément en élément (flèches), on
  // peut franchir une frontière de chapitre SANS changer d'écran. La colonne PROGRESSION doit
  // alors suivre l'élément ouvert (chapitre courant, dépliage, surlignage), pas rester figée sur
  // le chapitre d'entrée. L'élément porte son rang de chapitre dans ses params (cf. itemNav).
  const activeChapterId: number = Number(activeItem?.params?.chapterId ?? chapterId) || chapterId;

  // Résolution finale de la piste (élément ouvert > chapitre > mission) + démarrage du lecteur.
  // Le hook coupe le son à son démontage : quitter la navigation de la mission suffit.
  const activeRaw: any = (scenarioChapter?.sequence ?? []).find(
    (it: any) => String(it.id) === String(activeItem?.params?.itemId ?? ''));
  const musicUrl: string | null = activeRaw?.music ?? (scenarioChapter as any)?.music ?? missionMusic;
  useMissionMusic(storyKey, musicUrl);   // lecture ; la commande vit dans le header (CCT-136)
  // ⚠️ ÉTAT PROPRE À L'ENTRÉE DANS LE CHAPITRE. Une réplique dont le nettoyage n'a pas
  // tourné (onglet fermé en pleine lecture) laisserait le compteur d'atténuation au-dessus
  // de zéro pour toute la session — et la musique basse sans raison visible. Ici, aucune
  // réplique ne peut être en cours : rien n'est encore monté.
  useEffect(() => { resetDucking(); }, []);

  let dynSceneCount = 0;
  const buildSequenceFromScenario = (): SequenceItem[] => {
    if (!scenarioChapter) return [];
    // Personnages de la scène : ceux du SCÉNARIO, et eux seuls.
    const storyChars = scenario?.characters ?? [];
    return scenarioChapter.sequence.map((item: any, idx: number) => {
      const itemType = item.type === 'story' ? 'scene' : (item.type as SequenceItem['type']);
      const sourceId = typeof item.id === 'string' ? item.id : undefined;
      const blocking = !!item.blocking;   // barrière authoré (gating par élément, cf. serveur)
      if (itemType === 'scene') {
        dynSceneCount++;
        return {
          id: idx + 1, sourceId, type: 'scene' as const, title: item.title, blocking,
          description: typeof item.content === 'object' && 'narrativeText' in item.content
            ? String(item.content.narrativeText).split('\n\n')[0].replace(/\*\*/g, '') : undefined,
          estimatedDuration: `${item.duration ?? 5} min`,
          status: idx === 0 ? 'available' : 'locked',
          sceneNumber: dynSceneCount,
          // ⚠️ LES PERSONNAGES D'UNE SCÈNE SONT SES LOCUTEURS, pas le casting de la mission.
          //
          // Cette ligne prenait `storyChars.slice(0, 3)` : les trois PREMIERS personnages
          // déclarés, par ordre d'identifiant. La carte annonçait donc des visages qui ne
          // parlent pas dans la scène, et taisait ceux qui y parlent — sur la scène 1, elle
          // montrait Nina et Raoul, muets, et cachait Diane et Mina.
          //
          // Le serveur calcule pourtant la bonne liste (`content.characters` : les speakerColors
          // qui ont une fiche) ; elle était simplement ignorée. La branche `chat`, juste en
          // dessous, faisait déjà l'effort de remonter aux vrais participants.
          // Tous les speakerColors : la rangée d'avatars borne l'AFFICHAGE et annonce le reste.
          characters: (typeof item.content === 'object' && Array.isArray((item.content as any).characters)
            ? ((item.content as any).characters as string[])
            : []),
        } as SequenceItem;
      }
      if (itemType === 'chat') {
        // Mêmes avatars que pour une scène : les PARTICIPANTS du chat (locuteur + émetteurs des
        // messages). Le contenu porte des `characterId` ; on remonte au nom pour la vignette.
        const c: any = item.content ?? {};
        const idsIn = [c.tutorCharacterId, ...((c.messages ?? []).map((m: any) => m?.characterId))];
        const names: string[] = [];
        for (const cid of idsIn) {
          const found = storyChars.find((x: any) => x.id === cid || x.name === cid);
          const n = found?.name ?? (typeof cid === 'string' ? cid : '');
          if (n && !names.includes(n)) names.push(n);
        }
        return { id: idx + 1, sourceId, type: 'chat' as const, title: item.title, blocking,
          estimatedDuration: `${item.duration ?? 10} min`, status: idx === 0 ? 'available' : 'locked',
          characters: names } as SequenceItem;
      }
      if (itemType === 'card') return { id: idx + 1, sourceId, type: 'card' as const, title: item.title, blocking, estimatedDuration: `${item.duration ?? 5} min`, status: 'available' } as SequenceItem;
      if (itemType === 'quiz') return { id: idx + 1, sourceId, type: 'quiz' as const, title: item.title, blocking, estimatedDuration: `${item.duration ?? 5} min`, status: idx === 0 ? 'available' : 'locked' } as SequenceItem;
      return { id: idx + 1, sourceId, type: 'task' as const, title: item.title, blocking, estimatedDuration: `${item.duration ?? 15} min`, status: idx === 0 ? 'available' : 'locked' } as SequenceItem;
    });
  };


  // Contenu de jeu : UNIQUEMENT le scénario de CETTE mission (construit en base côté
  // runtime, cf. /me/stories/{id}/scenario). PAS de repli sur une histoire bundlée : une
  // mission sans storyline affichait sinon le contenu d'une AUTRE mission (Ego Crisis).
  const activeSequence: SequenceItem[] = buildSequenceFromScenario().map((item, index) => ({
    ...item, backendLocked: scenarioChapter?.sequence[index]?.locked === true,
  }));
  const activeObjectives: Objective[] = scenarioChapter?.learningObjectives?.map((title: string, i: number) => ({ id: i + 1, title, completed: false })) ?? [];

  // Gating par BARRIÈRE bloquante (miroir de `_gate_sequence` côté serveur) : un élément est
  // verrouillé s'il existe AVANT lui un élément `blocking` non complété. Un élément NON bloquant
  // ne verrouille PAS ses successeurs — le joueur peut avancer au-delà. La séquence pure
  // (« chaque élément exige le précédent ») était FAUSSE : elle bloquait tout, pas seulement
  // après une barrière.
  const getComputedStatus = (item: SequenceItem, index: number): 'locked' | 'available' | 'completed' => {
    return sequenceStatus(activeSequence, index, completedItems, true, scenario?.testMode);
  };

  const ch1Done = activeSequence.filter((item, index) => getComputedStatus(item, index) === 'completed').length;
  const ch1Total = activeSequence.length;
  const ch1Progress = ch1Total > 0 ? Math.round((ch1Done / ch1Total) * 100) : 0;

  // Chapitre terminé (100%) -> marque la complétion en BASE (débloque le suivant).
  // Une seule fois par montage ; nécessite storyChapterId + utilisateur connecté.
  useEffect(() => {
    if (!storyChapterId || gate !== 'ok' || completePostedRef.current) return;
    if (ch1Total > 0 && ch1Done >= ch1Total) {
      const token = getAccessToken();
      if (!user || !token) return;
      completePostedRef.current = true;
      apiFetch(`/me/stories/${missionId}/chapters/${storyChapterId}/complete`, {
        method: 'POST', token, auth: true,
      }).catch(() => { completePostedRef.current = false; });
    }
  }, [storyChapterId, gate, ch1Done, ch1Total, missionId, user, getAccessToken]);

  // ⚠️ NO FALLBACK ONTO ANOTHER MISSION'S CONTENT (same family as CCT-96 / CCT-253). These four
  // values used to be hard-coded: the certification badge read « AZ-104 », the title « Chapitre I -
  // Gouvernance Entra ID », the subtitle « Mise en place d'une gouvernance sécurisée ». A GCP
  // mission whose chapter carried no subtitle therefore displayed an Azure chapter's wording,
  // credibly and silently — the player had no way to tell it was not their story.
  //
  // The description was worse still: `missionId === 1 ? 'Nina arrive dans un studio Azure…'`
  // OVERRODE the real content rather than filling in for it, so an author editing that mission's
  // pitch in the studio changed nothing on screen. And nothing rendered it — it was dead code
  // carrying a hard-coded claim, so it is gone.
  //
  // What is missing is now simply ABSENT: the badge and the subtitle are only rendered when the
  // mission actually provides them.
  const certLabel = scenario?.certification ?? '';
  const chapterTitle = scenarioChapter?.title ?? t('chapter.fallbackTitle', { n: chapterIdx + 1 });
  const chapterSubtitle = scenarioChapter?.subtitle ?? '';

  const chapter: ChapterDetail = {
    // RESOLVED RANK (not the raw param): when arriving through `storyChapterId` (a notification),
    // `chapterId` holds its default of 1. The element screens therefore received `chapterId: 1`
    // and showed chapter 1's content — while the timeline showed the right chapter.
    id: chapterIdx + 1, title: chapterTitle, subtitle: chapterSubtitle,
    progress: ch1Progress, estimatedTime: 120, isUnlocked: true, isCompleted: ch1Progress === 100,
    objectives: activeObjectives, sequence: activeSequence,
  };

  // Équipe : personnages du SCÉNARIO (base) normalisés à la forme du bundle
  // ({name, role, avatar}) — `avatarUrl` distante → source {uri}. Repli bundle local.
  // TEAM = les personnages DÉCLARÉS de la mission (ceux du Story Builder), tels que servis
  // dans `scenario.characters`. Deux tentatives écartées : filtrer sur « qui parle » masquait
  // un personnage déclaré mais encore muet (Nour), et synthétiser les speakerColors non déclarés
  // inventait des membres. Le runtime sert désormais le MÊME jeu que le BO (même tenant), donc
  // cette liste est exactement celle du designer. Repli : bundle local.
  const storyChars: any[] = scenario?.characters?.length
    ? scenario.characters.map((c: any) => ({
        name: c.name, role: c.role ?? '',
        // `characterId` (numérique) sert à OUVRIR la conversation du moteur : `id` est une
        // clé narrative, elle ne désigne rien côté runtime (CCT-99).
        characterId: c.characterId ?? null,
        avatar: c.avatarUrl ? { uri: c.avatarUrl } : null,
        // CCT-148 : LE personnage joué par l'apprenant, désigné dans le Story Builder.
        isPlayer: !!c.isPlayer,
      }))
    : [];   // pas de scénario chargé => aucune équipe affichée, jamais une équipe bundlée

  // CCT-120 — bannière AUTHORÉE de la mission, choisie selon la surface : bannière mobile sur
  // écran étroit, bannière web sinon (le runtime applique déjà le repli mobile → web → widget).
  // L'image bundlée ne sert plus que de repli : elle est indexée par `storyKey`, qui retombe sur
  // « egocrisis » pour toute mission créée dans le BO — donc la mauvaise illustration.
  const missionBanner = (() => {
    const m: any = activeMissions.find((x: any) => x.id === missionId)
      ?? trialMissions.find((x: any) => x.id === missionId)
      ?? allMarketplaceMissions.find((x: any) => x.id === missionId);
    const img = isDesktop ? (m?.bannerWeb ?? m?.coverImage) : (m?.bannerMobile ?? m?.coverImage);
    return typeof img === 'string' ? img : img?.uri;
  })();
  const bannerImage = MISSION_BANNERS[storyKey] ?? FALLBACK_BANNER;

  // Ouvrir un élément doit donner le HAUT de l'élément : sans cela, le document garde le
  // défilement hérité de la timeline et l'élément apparaît déjà entamé.
  //
  // ⚠️ MAIS SEULEMENT QUAND LE DOCUMENT EST CE QUI DÉFILE. Sur la disposition BUREAU, le
  // lecteur s'ouvre dans SA colonne (`centerCol`) et démarre donc déjà en haut : remonter le
  // document en plus emportait toute la page — dont la colonne PROGRESSION, où le joueur
  // venait justement de cliquer. Il perdait sa position dans la liste à chaque ouverture,
  // et devait re-défiler pour ouvrir l'élément suivant.
  //
  // Le remède d'un cas (colonne unique, le document défile) était devenu la gêne de l'autre.
  useEffect(() => {
    if (!activeItem || isDesktop) return;
    if (typeof window !== 'undefined' && window.scrollTo) window.scrollTo(0, 0);
  }, [activeItem, isDesktop]);

  // ⚠️ ARMER À L'OUVERTURE, RELIRE À CHAQUE CHANGEMENT D'ÉLÉMENT (CCT-288). L'armement est
  // idempotent côté serveur : rouvrir un quiz ne redonne pas de temps. On relit ensuite la
  // liste complète, car un chronomètre de PORTÉE MISSION court en parallèle de celui de
  // l'élément — n'afficher que le dernier armé masquerait la seule échéance qui peut faire
  // perdre la partie entière.
  const itemOuvert: string | null = activeItem?.params?.itemId != null
    ? String(activeItem.params.itemId) : null;
  useEffect(() => {
    let annule = false;
    (async () => {
      const token = getAccessToken();
      if (!user || !token) return;
      if (itemOuvert) await startItemTimer(token, missionId, itemOuvert);
      if (!annule) await chargerTimers();
    })();
    return () => { annule = true; };
  }, [itemOuvert, missionId, user, chargerTimers]);

  // AN OPENING INTENT IS CONSUMED ONCE PER NAVIGATION, NOT ONCE PER SCREEN — CCT-349.
  //
  // ⚠️ MEASURED AS A REGRESSION. These intents (open this conversation, this task, this thread)
  // used to be guarded by a `useRef(false)` armed on arrival: it made sense while reaching the
  // screen and the intent happened together. Since the mission screen STAYS ON (CCT-346 shows the
  // messages and the board over it), a second click sent the same intent to a screen already
  // mounted — the ref said "already done", and the effects that key on the VALUE saw no change
  // either. Result: clicking a conversation, from the list or from a notification, opened nothing
  // at all, and a click that does nothing does not say why.
  //
  // React Navigation hands a NEW params object at every `navigate`, even for identical values. It
  // is therefore the object — the navigation event itself — that we consume: a repeated click
  // reopens, while a plain re-render (the scenario arriving, a resize) does not.
  const intentDone = useRef<object | null>(null);
  const intentConsumed = (p: any) => intentDone.current === p;
  const consumeIntent = (p: any) => { intentDone.current = p ?? null; };

  // Deep-link d'une notification du moteur (CCT-97) : `usChatElementId` ouvre directement le
  // chat de l'US DANS ce chapitre — le joueur garde le context de mission (barre latérale).
  const usChatElementId: string | null = route.params?.usChatElementId ?? null;
  useEffect(() => {
    if (usChatElementId && !intentConsumed(route.params)) {
      consumeIntent(route.params);
      setActiveItem({ type: 'us-chat', params: { missionId, elementId: usChatElementId } });
    }
  }, [route.params, usChatElementId, missionId]);

  // Centre de messagerie (CCT-97) : une conversation s'ouvre TOUJOURS dans le chapitre, pour
  // garder la barre latérale de mission. `characterChatId` = chronologie d'un personnage ;
  // `openFirstChat` = la discussion scénarisée de la mission (sinon on tombait sur la timeline).
  const characterChatId: number | null = route.params?.characterChatId ?? null;
  useEffect(() => {
    if (characterChatId != null && !intentConsumed(route.params)) {
      consumeIntent(route.params);
      setActiveItem({ type: 'character-chat', params: { characterId: characterChatId, missionId } });
    }
  }, [route.params, characterChatId, missionId]);

  // Ouverture d'un élément PRÉCIS (kanban du tableau de bord, CCT-98) : on ouvre l'US dans son
  // chapitre plutôt qu'un écran isolé, pour garder la barre latérale de mission.
  const openItemId: string | null = deepItemId;
  // Élément introuvable dans TOUTE la storyline (US orpheline d'un état d'écriture antérieur) :
  // on le DIT. Rester sur la timeline sans un mot ressemblait à un clic sans effet.
  // `deepItemMissing` peut venir du RESOLVER (il avait déjà le scénario au clic) ou être
  // constaté ici quand le blob arrive après coup.
  const [deepItemMissing, setDeepItemMissing] = useState(!!route.params?.deepItemMissing);
  useEffect(() => {
    if (route.params?.deepItemMissing) { setDeepItemMissing(true); return; }
    if (!deepItemId || !(scenario?.chapters?.length)) { setDeepItemMissing(false); return; }
    setDeepItemMissing(chapterIdxFromItem < 0);
  }, [deepItemId, chapterIdxFromItem, scenario, route.params?.deepItemMissing]);

  useEffect(() => {
    if (!openItemId || intentConsumed(route.params)) return;
    const item = chapter.sequence.find(it => String(it.sourceId ?? it.id) === String(openItemId));
    if (!item) return;                       // séquence pas encore chargée, ou autre chapitre
    consumeIntent(route.params);
    navigateToItem(item, 'available');
  }, [route.params, openItemId, chapter]);

  // CCT-122 — notification SANS élément précis (routes héritées `/mission/{id}/chapters/{cid}`,
  // encore nombreuses en base et jamais ré-émises car les beats sont dédoublonnés) : on ouvre le
  // 1er élément NON TERMINÉ du chapitre. Atterrir sur la timeline obligeait le joueur à
  // re-chercher lui-même l'étape que la notification venait de lui citer.
  // ⚠️ THE INTENT IS THE NAVIGATION EVENT, NOT THE MOUNT. A per-mount boolean (`pendingOpened`)
  // fired once and never again: inside a mission this screen stays MOUNTED, so the second
  // notification without a precise element (a launch, a chat announcement) did nothing and the
  // player was left on whatever the PREVIOUS notification had opened — measured in-app
  // (14-notifications-clics.mjs): six legacy clicks in a row, US-100-1 on screen every time,
  // while the same clicks after a reload each opened the right element. Same rule as
  // `openItemId` / `usChatElementId`: consume the params object handed by React Navigation.
  const openFirstPending: boolean = !!route.params?.openFirstPending;
  useEffect(() => {
    if (!openFirstPending || intentConsumed(route.params)) return;
    // Un élément explicitement visé par la route a la PRIORITÉ : sinon une relance d'US
    // ouvrait « le 1er élément non terminé » du chapitre — typiquement une scène.
    if (deepItemId) return;
    // ⚠️ Le scénario blob arrive APRÈS le 1er rendu : tant que le chapitre demandé n'est pas
    // résolu, `chapter` est le chapitre BUNDLÉ n°1. Ouvrir son 1er élément verrouillait un
    // choix définitif (`pendingOpened`) sur le mauvais chapitre — c'est ce qui faisait atterrir
    // sur « Scène 1 » du chapitre 1 alors que la notification citait le chapitre 2.
    if (storyChapterId && chapterIdxFromKey < 0) return;
    const seq = chapter.sequence ?? [];
    if (!seq.length) return;                 // séquence pas encore chargée (scénario blob)
    const item = seq.find((it, i) => getComputedStatus(it, i) === 'available')
      ?? seq.find(it => !completedItems.has(itemKey(it)));
    if (!item) return;                       // chapitre entièrement terminé : on reste sur la timeline
    consumeIntent(route.params);
    navigateToItem(item, 'available');
  }, [route.params, openFirstPending, chapter, completedItems, storyChapterId, chapterIdxFromKey, deepItemId]);

  const openFirstChat: boolean = !!route.params?.openFirstChat;
  useEffect(() => {
    if (!openFirstChat || intentConsumed(route.params)) return;
    const item = chapter.sequence.find(it => it.type === 'chat');
    if (!item) return;                       // séquence pas encore chargée (scénario blob)
    consumeIntent(route.params);
    navigateToItem(item, 'available');
  }, [route.params, openFirstChat, chapter]);

  const inlineNavigate = (screenName: string, params: any) => {
    const typeMap: Record<string, string> = {
      SceneReader: 'scene', MissionChat: 'chat', MissionCards: 'card', Quiz: 'quiz', TaskDetail: 'task',
      UsChat: 'us-chat',   // chat du moteur : reste DANS le chapitre (CCT-97)
    };
    const type = typeMap[screenName];
    if (type) {
      if (params?.chapterId != null && Number(params.chapterId) !== chapter.id) {
        navigation.setParams({ chapterId: Number(params.chapterId), storyChapterId: undefined,
          openItemId: undefined, usElementId: undefined });
      }
      setActiveItem({ type, params });
    }
    else { navigation.navigate(screenName as never, params as never); }
  };

  const navigateToItem = (item: SequenceItem, computedStatus: 'locked' | 'available' | 'completed') => {
    if (computedStatus === 'locked') return;
    const index = activeSequence.findIndex(candidate => itemKey(candidate) === itemKey(item));
    if (sequenceStatus(activeSequence, index, completedItems, true, scenario?.testMode) === 'locked') return;
    // CCT-164 — la séquence de navigation couvre TOUTE L'HISTOIRE, pas le seul chapitre :
    // arrivé au dernier élément d'un chapitre, « → » doit enchaîner sur le PREMIER du chapitre
    // suivant. En restant borné au chapitre, le joueur devait repasser par la timeline à chaque
    // frontière — et rien ne permettait de remonter au chapitre précédent.
    // On y ajoute le RANG du chapitre porteur : chaque saut sait dans quel chapitre il atterrit.
    const allChapters: any[] = (scenario?.chapters ?? []);
    const flat: any[] = allChapters.length
      ? buildNavSequence(allChapters)   // exclut déjà chapters et items verrouillés (CCT-258)
      : chapter.sequence
          // Repli SANS scénario servi : le verrou vient du statut calculé ici. Sans ce filtre,
          // la flèche « → » franchissait une barrière bloquante que le clic sur la timeline
          // refusait — deux chemins, une seule règle attendue.
          .filter((it, i) => getComputedStatus(it, i) !== 'locked')
          .map((it) => ({
            id: String(it.sourceId ?? it.id), type: it.type, title: it.title,
            sceneNumber: it.sceneNumber, chapterRank: chapter.id,
          }));
    const seq = chapter.sequence;
    const itemKeyStr = String(item.sourceId ?? item.id);
    const currentIndex = flat.findIndex((s: any) => String(s.id) === itemKeyStr);
    const itemSeq = flat;
    const itemIdParam = item.sourceId ?? item.id;
    const params: Record<string, any> = {
      missionId, chapterId: chapter.id, itemId: itemIdParam, title: item.title,
      // ⚠️ LES TITRES VOYAGENT AVEC L'ÉLÉMENT, POUR LA MÊME RAISON QUE LA SÉQUENCE (CCT-378).
      // Un écran d'élément ne connaît ni la mission ni le chapitre : il ne pouvait donc afficher
      // qu'un libellé générique, et chaque écran en avait inventé un différent. Les refaire
      // charger par chaque écran aurait multiplié les appels pour une donnée déjà en main ici.
      missionTitle: scenario?.title ?? '', chapterTitle: chapter.title,
      chapterSubtitle: chapter.subtitle ?? '',
      // La SÉQUENCE voyage avec l'élément (cf. app/utils/itemNav) : un écran ignore tout du
      // chapitre, il ne pouvait donc pas transmettre les voisins du voisin. En ne passant que
      // les voisins immédiats, le context mourait au premier « → » et l'élément suivant se
      // croyait dernier — son « ✓ » revenait à la timeline au lieu d'enchaîner.
      itemSeq, itemIndex: currentIndex >= 0 ? currentIndex : seq.findIndex(s => s.id === item.id),
    };
    if (isDesktop) {
      switch (item.type) {
        case 'scene': setActiveItem({ type: 'scene', params: { ...params, title: item.title, sceneNumber: item.sceneNumber } }); break;
        case 'chat':  setActiveItem({ type: 'chat', params }); break;
        case 'card':  setActiveItem({ type: 'card', params }); break;
        case 'quiz':  setActiveItem({ type: 'quiz', params }); break;
        case 'task':  setActiveItem({ type: 'task', params: { ...params, title: item.title } }); break;
      }
    } else {
      switch (item.type) {
        case 'scene': navigation.navigate('SceneReader' as never, { ...params, title: item.title, sceneNumber: item.sceneNumber } as never); break;
        case 'chat':  navigation.navigate('MissionChat' as never, params as never); break;
        case 'card':  navigation.navigate('MissionCards' as never, params as never); break;
        case 'quiz':  navigation.navigate('Quiz' as never, params as never); break;
        case 'task':  navigation.navigate('TaskDetail' as never, { ...params, title: item.title } as never); break;
      }
    }
  };

  // ⚠️ LA COMPLÉTION N'EST PLUS POSÉE ICI. Elle l'était sur le seul `onPress` de la timeline :
  // tout autre chemin menant au même élément — les flèches « ← / → », un lien profond de
  // notification, le retour arrière — l'ouvrait sans rien enregistrer. On pouvait traverser un
  // chapitre entier par la navigation et voir la progression rester à 1/12.
  //
  // La règle vit désormais dans `useElementVisited`, attachée à l'élément AFFICHÉ (cf.
  // utils/elementProgress). Un seul endroit, tous les chemins.
  const handleProgressionClick = (index: number) => {
    const item = chapter.sequence[index];
    if (!item) return;
    const computedStatus = getComputedStatus(item, index);
    if (computedStatus === 'locked') return;
    navigateToItem(item, computedStatus);
  };

  // ⚠️ THE PANELS BELOW ARE CALLED, NOT MOUNTED — `{Panel()}` and never `<Panel />`.
  //
  // They are declared inside this screen's body, so each render creates a NEW function
  // identity. Mounted as `<Panel />`, React sees a different component TYPE at that position,
  // unmounts the whole subtree and mounts a fresh one. The progression column lives inside a
  // `ScrollView`: remounting hands it a brand-new node, scrolled back to the top.
  //
  // That is the bug that survived a first repair. Clicking an item in the progression calls
  // `setActiveItem`, which re-renders this screen — so the player lost their place in the list
  // at the very moment they used it, and had to scroll down again for every single item. The
  // earlier fix only cancelled the mobile `window.scrollTo`, which is a different cause with
  // the same symptom; the desktop remount was left untouched.
  //
  // Calling them instead splices their elements straight into this tree: the `ScrollView`
  // keeps its identity across renders, and so does its scroll offset. None of them uses a
  // hook (checked), so a plain call is safe — should one ever need state, it must be hoisted
  // to a real module-level component rather than mounted from here.
  const DeepItemMissing = () => (!deepItemMissing ? null : (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, margin: 12, padding: 10,
      borderRadius: 10, borderWidth: 1, borderColor: C.orange, backgroundColor: `${C.orange}1F` }}>
      <Ionicons name="alert-circle-outline" size={16} color={C.orange} />
      <Text style={{ flex: 1, color: C.textPrim, fontSize: 12 }}>
        {t('chapter.staleDeepLink')}
      </Text>
    </View>
  ));

  const MissionHeader = () => (
    // ⚠️ SHORTER WHILE AN ELEMENT IS OPEN — CCT-352. It stays visible (title, progression,
    // countdown, back), but 180 px of banner above a conversation would eat the reading area:
    // the banner SITUATES what one reads, it must not take its place.
    <View style={[s.headerWrap, activeItem && s.headerWrapCompact]}>
      {/* CCT-136 — la commande du son a rejoint le HEADER de l'app (tout en haut à gauche) :
          sur le bandeau, elle disparaissait dès qu'un élément était ouvert alors que la piste
          continuait de jouer. */}
      <RemoteImage uris={[missionBanner, azuriteFallback(missionBanner)]} fallback={bannerImage}
        style={s.bannerImg} resizeMode="cover" />
      <View style={s.bannerOverlay} />
      <View style={s.bannerContent}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={s.backBtn}>
          <Ionicons name={"arrow-back"} size={20} color={C.textPrim} />
        </TouchableOpacity>
        <View style={s.bannerTitles}>
          <View style={s.threatRow}>
            <View style={s.threatBadge}>
              <Text style={s.threatText}>{t('chapter.threatLevel')}</Text>
            </View>
          </View>
          <Text style={s.missionTitle}>{chapter.title.toUpperCase()}</Text>
          {!!chapter.subtitle && <Text style={s.missionSub}>{chapter.subtitle}</Text>}
          <View style={s.certRow}>
            {!!certLabel && <View style={s.certBadge}><Text style={s.certText}>{certLabel}</Text></View>}
            {scenario?.testMode && <Text style={{ color: '#ffb454', fontWeight: '700' }}>Mode test · Navigation libre</Text>}
            <Text style={s.stepCount}>{t('chapter.steps', { done: ch1Done, total: ch1Total })}</Text>
            {/* Compte à rebours (CCT-288) — composant de MODULE, qui tique tout seul : porter
                la seconde dans l'état de cet écran le re-rendrait entièrement chaque seconde. */}
            <MissionCountdown timer={chronoAAfficher(timers, itemOuvert)} colors={C as any} t={t} />
          </View>
        </View>
      </View>
    </View>
  );

  const TimelineCard = ({ item, index }: { item: SequenceItem; index: number }) => {
    const computedStatus = getComputedStatus(item, index);
    const isLocked = computedStatus === 'locked';
    const isCompleted = computedStatus === 'completed';
    const isAvailable = computedStatus === 'available';
    const tc = TYPE_COLORS[item.type] ?? TYPE_COLORS.task;
    const num = String(index + 1).padStart(2, '0');
    const isActive = isAvailable && !isCompleted;
    // Élément OUVERT : même repère que dans la colonne de progression, pour ne pas perdre sa
    // position dans la séquence quand un écran s'ouvre à côté.
    const isSelected = !!activeItem && String(activeItem.params?.itemId ?? '')
      === String(item.sourceId ?? item.id);

    const actionLabel = t(isCompleted ? 'chapter.action.review'
      : item.type === 'scene' ? 'chapter.action.watch'
      : item.type === 'chat' ? 'chapter.action.openChat'
      : item.type === 'card' ? 'chapter.action.read'
      : 'chapter.action.start');
    const actionColor = isCompleted ? C.textSec : tc.text;
    const actionIcon = isCompleted ? 'eye-outline'
      : item.type === 'scene' ? 'play-circle-outline'
      : item.type === 'chat' ? 'chatbox-outline'
      : item.type === 'card' ? 'book-outline'
      : 'arrow-forward';

    return (
      <View
        style={s.timelineRow}
        onLayout={(e) => { itemPositions.current[index] = e.nativeEvent.layout.y; }}
      >
        <View style={s.timelineLeft}>
          <Text style={s.timelineNum}>{num}</Text>
          <View style={[s.timelineDot, {
            backgroundColor: isCompleted ? C.green : isAvailable ? tc.text : C.textMute,
            shadowColor: isCompleted ? C.green : isAvailable ? tc.text : 'transparent',
          }]} />
        </View>
        <View style={[s.card, isActive && { borderColor: tc.text + '55', shadowColor: tc.text },
          isLocked && s.cardLocked, isSelected && s.cardSelected]}>
          <View style={s.cardTopRow}>
            <View style={[s.typeBadge, { backgroundColor: tc.bg }]}>
              <Text style={[s.typeBadgeText, { color: tc.text }]}>{tc.label}</Text>
            </View>
            <View style={s.durationRow}>
              <Ionicons name={"time-outline"} size={11} color={C.textMute} />
              <Text style={s.durationText}>{item.estimatedDuration}</Text>
            </View>
            {isCompleted && (
              <View style={s.statusBadge}>
                <Ionicons name={"checkmark"} size={11} color={C.cyan} />
                <Text style={[s.statusText, { color: C.cyan }]}>{item.type === 'scene' || item.type === 'chat' ? 'VUE' : 'DONE'}</Text>
              </View>
            )}
            {isLocked && (
              <View style={[s.statusBadge, s.statusLocked]}>
                <Ionicons name={"lock-closed"} size={11} color={C.textMute} />
                <Text style={[s.statusText, { color: C.textMute }]}>VERROUILLE</Text>
              </View>
            )}
          </View>
          <Text style={[s.cardTitle, isLocked && { color: C.textMute }]}>{item.title}</Text>
          {item.description && !isLocked && (
            <Text style={s.cardDesc} numberOfLines={2}>{item.description}</Text>
          )}
          <View style={s.cardMeta}>
            {item.subtitle && <View style={s.codeChip}><Text style={s.codeText}>{item.subtitle}</Text></View>}
            {item.priority && (
              <View style={[s.priorityChip, { backgroundColor: PRIORITY_COLORS[item.priority] + '22' }]}>
                <Text style={[s.priorityText, { color: PRIORITY_COLORS[item.priority] }]}>{item.priority}</Text>
              </View>
            )}
            {item.characters && item.characters.length > 0 && !isLocked && (
              <View style={s.avatarsRight}>
                <SceneAvatarRow characters={item.characters} storyKey={storyKey} scenarioChars={scenario?.characters} />
              </View>
            )}
            {item.characterAvatar && !isLocked && (
              <Image source={item.characterAvatar} style={s.charAvatarSm} resizeMode={"cover"} />
            )}
          </View>
          {isLocked && (
            <View style={s.lockOverlay}>
              <Ionicons name={"lock-closed"} size={22} color={C.textMute} />
              <Text style={s.lockText}>{t('chapter.requiresPrevious')}</Text>
            </View>
          )}
          {!isLocked && (
            <TouchableOpacity
              style={[s.actionBtn, { borderColor: actionColor + '88', backgroundColor: isCompleted ? 'transparent' : tc.bg }]}
              onPress={() => navigateToItem(item, computedStatus)}
              activeOpacity={0.8}
            >
              <Text style={[s.actionBtnText, { color: actionColor }]}>{actionLabel}</Text>
              <Ionicons name={actionIcon as any} size={13} color={actionColor} />
            </TouchableOpacity>
          )}
        </View>
      </View>
    );
  };

  const TimelineColumn = () => (
    <View style={s.timelineWrap}>
      <View style={s.verticalLine} />
      {chapter.sequence.map((item, index) => (
        <React.Fragment key={item.id}>{TimelineCard({ item, index })}</React.Fragment>
      ))}
    </View>
  );

  // Chapter groups for accordion progression
  // La PROGRESSION liste les CHAPITRES DE LA MISSION (pas les scènes du chapitre courant) :
  // sans ça, un 2e chapitre ajouté par l'auteur n'apparaissait jamais, même verrouillé (CCT-102).
  const chapterGroups: ChapterGroup[] = useMemo(() => {
    const chs = scenario?.chapters ?? [];
    return chs.map((ch: any, i: number) => ({
      id: i + 1,
      title: ch.title || `Chapitre ${i + 1}`,
      // « Courant » = le chapitre de l'élément OUVERT (activeChapterId), pas celui d'entrée : la
      // colonne suit ainsi la navigation qui traverse les chapters.
      isCurrent: (i + 1) === activeChapterId,
      backendLocked: typeof ch.locked === 'boolean' ? ch.locked : undefined,
      items: (ch.sequence ?? []).map((it: any) => ({
        id: String(it.id ?? ''), type: it.type, title: it.title ?? '',
        // La barrière authorée voyage avec l'item : le repli local en a besoin pour appliquer
        // la MÊME règle que `_chapter_done` côté serveur.
        blocking: !!it.blocking,
        backendLocked: it.locked === true,
      })),
    }));
  }, [scenario, activeChapterId]);

  const [expandedChapters, setExpandedChapters] = React.useState<Record<number, boolean>>({ [chapterId]: true });
  const toggleChapter = (id: number) => setExpandedChapters(prev => ({ ...prev, [id]: !prev[id] }));
  // Synchronisation timeline → progression : dès qu'un élément d'un autre chapitre s'ouvre, on
  // déplie ce chapitre pour que l'élément surligné soit visible (l'autre sens — clic dans la
  // colonne → ouverture — existe déjà via openFromProgression).
  React.useEffect(() => {
    setExpandedChapters(prev => (prev[activeChapterId] ? prev : { ...prev, [activeChapterId]: true }));
  }, [activeChapterId]);

  const isChapterUnlocked = (group: ChapterGroup): boolean => {
    if (group.id === 1) return true;
    // AUTORITÉ = le backend : il ne livre le CONTENU que des chapters débloqués (les autres
    // n'ont qu'un squelette). Recalculer localement depuis completedItems désynchronise l'UI
    // du contenu réellement disponible (l'app croirait débloqué un chapitre servi en coquille).
    if (typeof group.backendLocked === 'boolean') return !group.backendLocked;
    const prev = chapterGroups[group.id - 2];
    if (!prev) return true;
    // ⚠️ REPLI ALIGNÉ SUR LE SERVEUR : les items BLOQUANTS, pas tous. C'est l'AUTEUR qui
    // choisit au builder ce qui verrouille — la case « bloquant » existe sur tous les types
    // sauf personnage et contexte, et les réglages d'histoire la laissent à `false`. Exiger la
    // totalité obligeait à ouvrir chaque fiche et chaque chat pour voir la suite.
    //
    // ⚠️ ET DEUX DÉFINITIONS DU MÊME FAIT FINISSENT TOUJOURS PAR DIVERGER. Ce repli ne sert que
    // si le serveur ne dit rien (`backendLocked` absent) ; s'il applique une AUTRE règle que
    // `_chapter_done`, l'app affiche un verrou que le serveur ignore, ou l'inverse.
    if (prev.items.length === 0) return false;
    const bloquants = prev.items.filter((it) => it.blocking);
    return bloquants.every((it) => completedItems.has(it.id));
  };

  const getChapterProgress = (group: ChapterGroup): number => {
    const done = group.items.filter((_, index) => sequenceStatus(group.items, index, completedItems, isChapterUnlocked(group), scenario?.testMode) === 'completed').length;
    return group.items.length > 0 ? Math.round((done / group.items.length) * 100) : 0;
  };

  // Ouvrir un élément depuis la progression : dans le chapitre DE L'ÉCRAN on l'ouvre en place ;
  // dans un AUTRE chapitre (débloqué) on navigue vers lui. On teste `group.id === chapterId`
  // (chapitre d'écran) et non `isCurrent` : après un franchissement par les flèches, le chapitre
  // « courant » est celui de l'élément ouvert, mais `chapter.sequence` reste celui de l'écran —
  // indexer dedans ouvrirait le mauvais élément.
  const openFromProgression = (group: ChapterGroup, itemId: string) => {
    const index = group.items.findIndex(item => item.id === itemId);
    if (sequenceStatus(group.items, index, completedItems, isChapterUnlocked(group), scenario?.testMode) === 'locked') return;
    if (group.id === chapterId) {
      const idx = chapter.sequence.findIndex(s => String(s.sourceId ?? s.id) === itemId);
      if (idx >= 0) handleProgressionClick(idx);
      return;
    }
    navigation.navigate('ChapterDetail', { missionId, chapterId: group.id, openItemId: itemId });
  };

  // Entête de progression : compteur et jauge du chapitre RÉELLEMENT ouvert (activeChapterId).
  const headerGroup = chapterGroups.find(g => g.id === activeChapterId);
  const headDone = headerGroup ? headerGroup.items.filter((_, index) => sequenceStatus(headerGroup.items, index, completedItems, isChapterUnlocked(headerGroup), scenario?.testMode) === 'completed').length : ch1Done;
  const headTotal = headerGroup ? headerGroup.items.length : ch1Total;
  const headPct = headTotal > 0 ? Math.round((headDone / headTotal) * 100) : 0;

  const ProgressionPanel = () => (
    <View style={s.panelWrap}>
      <Text style={s.panelTitle}>{t('notif.filter.chapters')} ({chapterGroups.length})</Text>
      <View style={s.progTrack}><View style={[s.progFill, { width: `${headPct}%` as any }]} /></View>
      <Text style={[s.progSceneLabel, { marginTop: 6 }]}>{headDone}/{headTotal} etapes</Text>
      <View style={{ marginTop: 10, gap: 4 }}>
        {chapterGroups.map(group => {
          const unlocked = isChapterUnlocked(group);
          const expanded = expandedChapters[group.id] ?? false;
          const pct = getChapterProgress(group);
          const allDone = pct === 100;
          return (
            <View key={group.id} style={s.chapterAccordion}>
              <TouchableOpacity
                style={[s.chapterHeader, !unlocked && s.chapterHeaderLocked]}
                onPress={() => {
                  // Verrouillé : on déplie quand même pour PRÉVISUALISER ce qui vient (CCT-102).
                  // On déplie sur place le chapitre d'écran ET le chapitre courant (celui de
                  // l'élément ouvert) ; les autres débloqués → navigation.
                  if (!unlocked || group.id === chapterId || group.isCurrent) toggleChapter(group.id);
                  else {
                    // Changing chapters must close the previous inline reader.
                    setActiveItem(null);
                    setMobileTab('timeline');
                    navigation.navigate('ChapterDetail', { missionId, chapterId: group.id,
                      itemId: undefined, storyChapterId: undefined, openFirstPending: undefined });
                  }
                }}
                activeOpacity={0.7}
              >
                <View style={[s.chapterNum, { backgroundColor: allDone ? C.green + '22' : unlocked ? 'rgba(0,240,255,0.1)' : 'rgba(255,255,255,0.04)' }]}>
                  {unlocked
                    ? <Text style={[s.chapterNumText, { color: allDone ? C.green : C.cyan }]}>{String(group.id).padStart(2,'0')}</Text>
                    : <Ionicons name={"lock-closed"} size={10} color={C.textMute} />}
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={[s.chapterTitle, !unlocked && { color: C.textMute }]} numberOfLines={1}>{group.title}</Text>
                  <View style={s.chapterProgRow}>
                    <View style={s.chapterProgTrack}>
                      <View style={[s.chapterProgFill, { width: `${pct}%` as any, backgroundColor: allDone ? C.green : C.cyan }]} />
                    </View>
                    <Text style={[s.chapterProgPct, { color: allDone ? C.green : C.textMute }]}>{pct}%</Text>
                  </View>
                </View>
                <Ionicons name={expanded ? "chevron-up" : "chevron-down"} size={12} color={C.textMute} />
              </TouchableOpacity>
              {expanded && (
                <View style={s.chapterItems}>
                  {group.items.map((item, idx) => {
                    // The exact state (blocking barriers) is computed on THIS GROUP'S OWN items,
                    // for every chapter alike. It used to go through `getComputedStatus`, which
                    // walks `activeSequence` — the OPEN chapter's — with the index of an item of
                    // the chapter of the SCREEN. The two differ after a deep link or an arrow
                    // crossing: on SimBiose, chapter 2 open (11 items) and chapter 1 walked (13)
                    // read `activeSequence[11].blocking` on `undefined` → "Something went wrong"
                    // over the whole screen (CCT-433). Same rule as `_gate_sequence` server-side.
                    const cur = sequenceStatus(group.items, idx, completedItems, unlocked, scenario?.testMode);
                    const done = cur === 'completed';
                    const dotColor = done ? C.green : cur === 'available' ? C.orange : C.textMute;
                    // Surlignage de l'élément OUVERT, quel que soit son chapitre (sync timeline → progression).
                    const isSelected = group.isCurrent && !!activeItem
                      && String(activeItem.params?.itemId ?? '') === item.id;
                    return (
                      <TouchableOpacity
                        key={item.id}
                        style={[s.progItem, { paddingLeft: 8, paddingVertical: 4 },
                          isSelected && s.progItemSelected]}
                        disabled={cur === 'locked'}
                        onPress={() => cur !== 'locked' && openFromProgression(group, item.id)}
                        activeOpacity={cur === 'locked' ? 1 : 0.7}
                      >
                        <View style={[s.progDot, { backgroundColor: dotColor }]} />
                        {/* Pastille de NATURE : ce qu'on s'apprête à ouvrir. Les titres sont
                            tronqués sur une ligne, et rien ne distinguait une scène d'un chat
                            ou d'une fiche avant de cliquer. Elle reste grisée tant que
                            l'élément n'est pas accessible — l'icône informe, elle ne promet
                            pas que c'est ouvrable. */}
                        <Ionicons
                          name={(TYPE_ICONE[item.type] ?? 'ellipse-outline') as any}
                          size={10}
                          color={(!done && cur === 'locked')
                            ? C.textMute
                            : (TYPE_COLORS[item.type]?.text ?? C.textSec)}
                        />
                        <Text
                          style={[s.progItemText,
                            { color: (!done && cur === 'locked') ? C.textMute : C.textSec },
                            isSelected && s.progItemTextSelected]}
                          numberOfLines={1}
                        >
                          {String(idx + 1).padStart(2, '0')}  {item.title}
                        </Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          );
        })}
      </View>
    </View>
  );

  const TeamPanel = () => (
    <View style={s.panelWrap}>
      <Text style={s.panelTitle}>{t('chapter.panel.team')}</Text>
      {/* Le JOUEUR n'a pas sa place dans l'équipe : la liste sert à ENGAGER une conversation,
          et on ne discute pas avec soi-même (CCT-154). Il reste visible dans « Personnages »,
          où l'on montre le casting. */}
      {storyChars.filter((c: any) => !c.isPlayer).slice(0, 6).map(char => {
        return (
          <View key={char.name} style={s.teamRow}>
            {(char.isPlayer ? (playerAvatar ?? char.avatar) : char.avatar)
              ? <Image source={char.isPlayer ? (playerAvatar ?? char.avatar) : char.avatar}
                  style={[s.teamAvatar, char.isPlayer && s.teamAvatarMe]} resizeMode={"cover"} />
              : <View style={[s.teamAvatar, s.teamAvatarFb, char.isPlayer && s.teamAvatarMe]}><Text style={s.teamAvatarInit}>{char.name[0]}</Text></View>}
            <View style={s.teamInfo}>
              {/* CCT-152 — le joueur se voit LUI-MÊME dans l'équipe (son nom, son avatar) ;
                  le personnage qu'il incarne apparaît en rôle. Afficher « Samir » à sa place
                  revenait à lui présenter un inconnu comme étant lui. */}
              <Text style={[s.teamName, char.isPlayer && { color: C.cyan }]}>
                {char.isPlayer ? (playerName || 'Vous') : char.name}
              </Text>
              {/* CCT-153 : le rôle affiché est celui du PERSONNAGE tenu, pour tous. */}
              <Text style={s.teamRole}>{char.role}</Text>
            </View>
            <TouchableOpacity
              style={s.chatIconBtn}
              // Le chat d'équipe ouvre la VRAIE conversation du moteur dans un dialogue
              // (CCT-99) : l'ancien panneau local répondait « Message recu » et ne laissait
              // aucune trace. Sans `characterId`, le personnage n'a pas d'existence runtime.
              onPress={() => setTeamChat({ characterId: char.characterId, name: char.name })}
              activeOpacity={0.7}
            >
              <Ionicons name={"chatbubble-outline"} size={16} color={C.cyan} />
            </TouchableOpacity>
          </View>
        );
      })}
    </View>
  );

  // RewardsPanel / ObjectivesPanel supprimés (CCT-99) : contenu EN DUR, identique pour
  // toutes les missions — ils occupaient la colonne sans rien apprendre au joueur.

  // ⚠️ CE BOUTON N'APPELAIT RIEN. `onPress={() => navigation.goBack()}` : aucune requête, aucun
  // changement d'état. Depuis une scène, le retour ramène au chapitre — l'action la plus
  // engageante de l'écran donnait donc l'impression de ne rien faire, et ne faisait rien.
  //
  // ⚠️ ET ELLE SE CONFIRME. Abandonner clôt la partie : le joueur devra la relancer, et ce
  // nouveau départ repartira de zéro. Un clic accidentel sur un bouton rouge en bas d'une
  // colonne ne doit pas suffire.
  const AbandonBtn = () => (
    <TouchableOpacity style={s.abandonBtn} onPress={() => setAbandonAsk(true)} activeOpacity={0.8}>
      <Ionicons name={"exit-outline"} size={16} color={C.red} />
      <Text style={s.abandonText}>{t('chapter.abandonOperation')}</Text>
    </TouchableOpacity>
  );

  const AbandonDialog = () => (
    <Modal visible={abandonAsk} transparent animationType="fade"
           onRequestClose={() => setAbandonAsk(false)}>
      <View style={s.modalBackdrop}>
        <View style={[s.modalCard, { backgroundColor: C.card }]}>
          <Text style={{ color: C.textPrim, fontSize: 17, fontWeight: '700', marginBottom: 8 }}>
            {t('chapter.abandonTitle')}
          </Text>
          <Text style={{ color: C.textSec, fontSize: 13, lineHeight: 19, marginBottom: 18 }}>
            {t('chapter.abandonBody')}
          </Text>
          <TouchableOpacity
            disabled={abandoning}
            onPress={async () => {
              setAbandoning(true);
              const r = await abandonMissionRun(getAccessToken(), missionId);
              setAbandoning(false);
              setAbandonAsk(false);
              // ⚠️ ON NE SORT QUE SI C'EST FAIT. Revenir en arrière sur un échec laisserait le
              // joueur croire sa partie close alors qu'elle tourne toujours — c'est
              // exactement le défaut qu'on corrige.
              //
              // ⚠️ ET LA DESTINATION EST EXPLICITE, PAS UN RETOUR EN ARRIÈRE. Mesuré : sur une
              // entrée par URL directe il n'y a AUCUN historique — « GO_BACK was not handled
              // by any navigator » — et le joueur restait sur l'écran d'une partie qu'il
              // venait de clore. Un abandon doit mener quelque part, pas dépendre de la façon
              // dont on est arrivé.
              if (r.abandoned) navigation.navigate('Missions' as never);
              else setAbandonFailed(true);
            }}
            style={[s.abandonBtn, { margin: 0 }]}>
            <Text style={s.abandonText}>
              {abandoning ? t('chapter.abandoning') : t('chapter.abandonConfirm')}
            </Text>
          </TouchableOpacity>
          {abandonFailed && (
            <Text style={{ color: C.red, fontSize: 12.5, marginTop: 12, textAlign: 'center' }}>
              {t('chapter.abandonFailed')}
            </Text>
          )}
          <TouchableOpacity onPress={() => setAbandonAsk(false)}
                            style={{ alignItems: 'center', marginTop: 14, padding: 8 }}>
            <Text style={{ color: C.textSec }}>{t('common.cancel')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const RightSidebar = () => (
    <View style={s.rightSidebar}>
      <ScrollView showsVerticalScrollIndicator={false}>
        {ProgressionPanel()}
        {TeamPanel()}
        {AbandonBtn()}
        <View style={{ height: 80 }} />
      </ScrollView>
    </View>
  );

  const MobileTabBar = () => (
    <View style={s.mobileTabBar}>
      {(['timeline', 'team', 'progression'] as const).map(tab => (
        <TouchableOpacity
          key={tab}
          style={[s.mobileTabItem, mobileTab === tab && s.mobileTabItemActive]}
          onPress={() => setMobileTab(tab)}
          activeOpacity={0.7}
        >
          <Text style={[s.mobileTabText, mobileTab === tab && s.mobileTabTextActive]}>
            {tab === 'progression'
              ? `${t('notif.filter.chapters')} (${chapterGroups.length})`
              : t(tab === 'timeline' ? 'chapter.tab.timeline' : 'chapter.tab.team')}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  if (gate === 'error') {
    return (
      <SafeAreaView style={[s.root, { alignItems: 'center', justifyContent: 'center', padding: 24 }]}>
        <Text style={{ color: C.textPrim, textAlign: 'center' }}>{t('chapter.loadFailed')}</Text>
        <TouchableOpacity onPress={() => setStartTick(n => n + 1)} style={{ padding: 20 }}>
          <Text style={{ color: C.cyan }}>{t('common.retry')}</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  // AUCUN SCÉNARIO SERVI (CCT-253). Depuis la suppression du repli bundlé, `scenario` peut
  // être absent : la mission n'a pas de storyline publiée dans cet environnement. L'écran
  // affichait alors une timeline VIDE, sans un mot — c'est-à-dire le même défaut silencieux
  // qu'avant, déplacé d'un cran. On le dit.
  // ⚠️ ET SEULEMENT SI LA PARTIE EST LANCÉE. Une partie non démarrée n'a PAS de scénario
  // chargé — la passerelle sort avant de le demander, exprès. Sans cette condition, l'écran
  // « pas de contenu » gagnait la course et accusait l'AUTEUR (« sa storyline n'est pas
  // publiée ») d'un défaut qui n'existe pas : mesuré en navigateur, sur un joueur simplement
  // pas inscrit à la mission. Un diagnostic faux coûte plus cher qu'un écran muet.
  if (gate !== 'loading' && gate !== 'not_started' && !scenario) {
    return (
      <SafeAreaView style={[s.root, { alignItems: 'center', justifyContent: 'center', padding: 24 }]} edges={['top', 'left', 'right']}>
        <Ionicons name="book-outline" size={48} color={C.textSec} />
        <Text style={{ color: C.textPrim, fontSize: 18, fontWeight: '700', marginTop: 16, textAlign: 'center' }}>
          {t('chapter.noContent')}
        </Text>
        <Text style={{ color: C.textSec, fontSize: 14, marginTop: 8, textAlign: 'center', maxWidth: 460, lineHeight: 20 }}>
          {t('chapter.noContentBody')}
        </Text>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ marginTop: 20, backgroundColor: C.cyan, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 }}>
          <Text style={{ color: '#001018', fontWeight: '700' }}>{t('chapter.back')}</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  // Passerelle de déblocage : chargement / chapitre verrouillé.
  if (gate === 'loading') {
    return (
      <SafeAreaView style={[s.root, { alignItems: 'center', justifyContent: 'center' }]} edges={['top', 'left', 'right']}>
        <ActionLoader label={t('chapter.loading')} />
      </SafeAreaView>
    );
  }
  // ⚠️ ON REFUSE DE RENDRE, ET ON DIT POURQUOI. Le contenu resterait techniquement lisible,
  // mais dans la langue d'origine, avec un moteur qui ne connaît aucune user story et une
  // progression suivie nulle part : c'est exactement l'écran crédible-mais-vide que le retrait
  // du repli bundlé (CCT-253) visait à supprimer.
  if (gate === 'not_started') {
    return (
      <SafeAreaView style={[s.root, { alignItems: 'center', justifyContent: 'center', padding: 24 }]} edges={['top', 'left', 'right']}>
        <Ionicons name="play-circle-outline" size={48} color={C.textSec} />
        <Text style={{ color: C.textPrim, fontSize: 18, fontWeight: '700', marginTop: 16, textAlign: 'center' }}>
          {t('chapter.notStarted')}
        </Text>
        <Text style={{ color: C.textSec, fontSize: 14, marginTop: 8, textAlign: 'center', maxWidth: 420 }}>
          {enrolled ? t('chapter.notStartedHint') : t('chapter.notEnrolledHint')}
        </Text>
        {enrolled ? (
          <TouchableOpacity
            onPress={() => { void launch(missionId, () => setStartTick((n) => n + 1)); }}
            style={{ marginTop: 20, backgroundColor: C.cyan, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 }}>
            <Text style={{ color: '#001018', fontWeight: '700' }}>{t('chapter.startNow')}</Text>
          </TouchableOpacity>
        ) : (
          <TouchableOpacity onPress={() => navigation.goBack()}
            style={{ marginTop: 20, backgroundColor: C.cyan, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 }}>
            <Text style={{ color: '#001018', fontWeight: '700' }}>{t('chapter.backToChapters')}</Text>
          </TouchableOpacity>
        )}
        {launchDialog}
      </SafeAreaView>
    );
  }
  if (gate === 'locked') {
    return (
      <SafeAreaView style={[s.root, { alignItems: 'center', justifyContent: 'center', padding: 24 }]} edges={['top', 'left', 'right']}>
        <Ionicons name="lock-closed" size={48} color={C.textSec} />
        <Text style={{ color: C.textPrim, fontSize: 18, fontWeight: '700', marginTop: 16, textAlign: 'center' }}>{t('chapter.locked')}</Text>
        <Text style={{ color: C.textSec, fontSize: 14, marginTop: 8, textAlign: 'center' }}>
          {t('chapter.lockedHint')}
        </Text>
        <TouchableOpacity onPress={() => navigation.goBack()} style={{ marginTop: 20, backgroundColor: C.cyan, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 }}>
          <Text style={{ color: '#001018', fontWeight: '700' }}>{t('chapter.backToChapters')}</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={s.root} edges={['top', 'left', 'right']}>
      {/* ⚠️ MONTÉ UNE FOIS, AU NIVEAU DE L'ÉCRAN. Le bouton d'abandon a DEUX emplacements —
          colonne de bureau et onglet mobile — et un dialogue monté à côté de l'un ne s'affiche
          pas quand c'est l'autre qui est rendu : la confirmation aurait manqué sur mobile. */}
      {AbandonDialog()}
      <View style={s.mainArea}>
        {isDesktop ? (
          <View style={s.desktopCols}>
            <View style={s.centerCol}>
              {/* ⚠️ LE BANDEAU DE MISSION RESTE VISIBLE, ÉLÉMENT OUVERT OU NON — CCT-352. Il ne
                  s'affichait qu'au-dessus de la timeline : dès qu'on ouvrait une scène, un chat
                  ou une tâche, le joueur perdait le titre de la mission, sa progression (« 8/13
                  étapes ») et le compte à rebours — c'est-à-dire tout ce qui situe ce qu'il est
                  en train de lire. Le retour en arrière du bandeau reste le SEUL chemin visible
                  vers la timeline quand on est plongé dans un élément. */}
              {MissionHeader()}
              {activeItem ? (
                <View style={{ flex: 1, minHeight: 0 }}>
                  {activeItem.type === 'scene' && (
                    <SceneReaderScreen
                      key={`${missionId}:${activeItem.params.itemId}`}
                      onProgressConfirmed={refreshAfterDialogue}
                      navigation={{ goBack: () => setActiveItem(null), navigate: (s: string, p: any) => inlineNavigate(s, p) } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {activeItem.type === 'chat' && (
                    <MissionChatScreen
                      key={`${missionId}:${activeItem.params.itemId}`}
                      onProgressConfirmed={refreshAfterDialogue}
                      navigation={{ goBack: () => setActiveItem(null), navigate: (s: string, p: any) => inlineNavigate(s, p) } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {/* Chat du MOTEUR (hint / refus / validation d'une US) — CCT-97 : rendu ICI pour
                      bénéficier du layout de mission (barre latérale progression/team/récompenses),
                      au lieu d'un écran /us-chat isolé. */}
                  {activeItem.type === 'character-chat' && (
                    <CharacterChatScreen
                      navigation={{ ...navigation, goBack: () => setActiveItem(null) }}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {activeItem.type === 'us-chat' && (
                    <UsChatScreen
                      onProgressConfirmed={refreshAfterDialogue}
                      navigation={{ goBack: () => setActiveItem(null), navigate: (s: string, p: any) => inlineNavigate(s, p) } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {activeItem.type === 'task' && (
                    <TaskDetailScreen
                      navigation={{ goBack: () => setActiveItem(null), navigate: (s: string, p: any) => inlineNavigate(s, p) } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {activeItem.type === 'quiz' && (
                    <QuizScreen
                      navigation={{ goBack: () => setActiveItem(null), navigate: (s: string, p: any) => inlineNavigate(s, p) } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                  {activeItem.type === 'card' && (
                    <MissionCardsScreen
                      onProgressConfirmed={refreshAfterDialogue}
                      navigation={{ goBack: () => setActiveItem(null), navigate: (s: string, p: any) => inlineNavigate(s, p) } as any}
                      route={{ params: activeItem.params } as any}
                    />
                  )}
                </View>
              ) : (
                <ScrollView ref={timelineScrollRef} style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
                  {DeepItemMissing()}
                  <View style={s.centerContent}>
                    {TimelineColumn()}
                  </View>
                  <View style={{ height: 80 }} />
                </ScrollView>
              )}
            </View>
            {RightSidebar()}
          </View>
        ) : (
          <View style={{ flex: 1 }}>
            {MissionHeader()}
            {DeepItemMissing()}
            {MobileTabBar()}
            <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
              <View style={s.mobilePad}>
                {mobileTab === 'timeline' && (
                  <>
                    {TimelineColumn()}
                    <View style={{ height: 12 }} />
                    {AbandonBtn()}
                  </>
                )}
                {mobileTab === 'team' && (
                  <>
                    {TeamPanel()}
                    <View style={{ height: 12 }} />
                  </>
                )}
                {mobileTab === 'progression' && (
                  <>
                    {ProgressionPanel()}
                    <View style={{ height: 12 }} />
                  </>
                )}
                <View style={{ height: 80 }} />
              </View>
            </ScrollView>
          </View>
        )}
      </View>

      {/* Dialogue d'équipe (CCT-99) : la VRAIE conversation du moteur avec ce personnage,
          dans une modale — l'ancien panneau local répondait « Message recu » sans rien garder. */}
      <Modal
        visible={!!teamChat}
        transparent
        animationType={"fade"}
        onRequestClose={() => setTeamChat(null)}
      >
        <TouchableOpacity style={s.modalBackdrop} activeOpacity={1} onPress={() => setTeamChat(null)}>
          <TouchableOpacity style={s.teamChatBox} activeOpacity={1} onPress={() => {}}>
            {teamChat?.characterId ? (
              <CharacterChatScreen
                navigation={{ ...navigation, goBack: () => setTeamChat(null) }}
                route={{ params: { characterId: teamChat.characterId, missionId } } as any}
              />
            ) : (
              <View style={s.teamChatEmpty}>
                <Text style={s.teamChatEmptyText}>
                  {t('chapter.characterNoIdentity', { name: teamChat?.name ?? '' })}
                </Text>
                <TouchableOpacity onPress={() => setTeamChat(null)} style={s.teamChatClose}>
                  <Text style={s.teamChatCloseText}>{t('chapter.close')}</Text>
                </TouchableOpacity>
              </View>
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* CCT-346 — NOTIFICATIONS / CONVERSATIONS / TABLEAU DE BORD, OVER the mission.
          Nothing is unmounted and nothing loses focus, so the music keeps playing and the open
          element, the scroll and the progression are all still there on closing.

          ⚠️ THE NAVIGATION IS PROXIED, and that is what makes the panel behave like a screen. The
          three read `useNavigation()` themselves (no props): served as is, a notification opening
          a task would push a screen UNDER this modal. Any navigation therefore closes the panel
          first, and "back" simply closes it. */}
      {/* ⚠️ NO FADE ON THE DROPDOWN, AND THAT IS A WEB CONSTRAINT. A React Native modal covers the
          WHOLE viewport, transparent or not; fading it out fades the entire screen with it, which
          reads as a flash of the whole page when the notification box closes. A box hanging under
          its bell must appear and disappear where it is, not repaint the app. The two real panels
          keep their fade: they do cover the screen, so the transition describes what happens. */}
      <Modal
        visible={!!missionPanel}
        transparent
        animationType={isDesktop && missionPanel === 'Notifications' ? 'none' : 'fade'}
        onRequestClose={() => setMissionPanel(null)}
      >
        {/* ⚠️ THE NOTIFICATION CENTRE IS A DROPDOWN, NOT A ROOM (CCT-346). On a large screen it
            hangs UNDER its own bell — one reads three lines and goes back to the mission; a
            full-width box for that is a detour. The other two are genuine screens (a six-column
            board, a conversation list), and on a phone every panel takes the width it can get.

            The backdrop closes the dropdown, and ONLY the dropdown: the board carries a
            drag-and-drop, where a gesture ending outside the box would close the panel mid-move. */}
        {(() => {
          const dropdown = isDesktop && missionPanel === 'Notifications';
          const contenu = (
            <View style={dropdown ? s.panelDropdown : s.panelBox}>
              <TouchableOpacity onPress={() => setMissionPanel(null)} style={s.panelClose}
                accessibilityLabel={t('chapter.close')}>
                <Ionicons name="close" size={20} color={C.textPrim} />
              </TouchableOpacity>
              <NavigationContext.Provider value={panelNav}>
                {missionPanel === 'Notifications' && (
                  // Opening a notification closes the panel — it routes through the global nav
                  // ref, so nothing else here would ever know it left.
                  <NotificationsScreen onOpened={() => setMissionPanel(null)} />
                )}
                {missionPanel === 'Chat' && <ChatScreen />}
                {missionPanel === 'Dashboard' && <DashboardScreen />}
              </NavigationContext.Provider>
            </View>
          );
          return dropdown ? (
            <TouchableOpacity activeOpacity={1} style={s.panelDropdownBackdrop}
              onPress={() => setMissionPanel(null)}>
              <TouchableOpacity activeOpacity={1} onPress={() => {}}>{contenu}</TouchableOpacity>
            </TouchableOpacity>
          ) : (
            <View style={s.panelBackdrop}>{contenu}</View>
          );
        })()}
      </Modal>
    </SafeAreaView>
  );
}

const makeStyles = (C: Palette) => StyleSheet.create({
  root: { flex: 1, minHeight: 0, backgroundColor: C.bg },
  mainArea: { flex: 1, minHeight: 0 },
  desktopCols: { flex: 1, flexDirection: 'row', minHeight: 0 },
  centerCol: { flex: 1, minHeight: 0 },
  centerContent: { paddingHorizontal: 24, paddingTop: 16 },
  // Repère FRANC (liseré + fond) : une simple nuance de texte se confond avec l'état
  // « verrouillé », qui est déjà gris.
  progItemSelected: { backgroundColor: 'rgba(0,240,255,0.14)', borderRadius: 6,
                      borderTopLeftRadius: 0, borderBottomLeftRadius: 0,
                      borderLeftWidth: 3, borderLeftColor: C.cyan },
  progItemTextSelected: { color: C.cyan, fontWeight: '700' },
  rightSidebar: { width: 300, borderLeftWidth: 1, borderLeftColor: C.border },
  teamChatBox: { width: '92%', maxWidth: 760, height: '82%', borderRadius: 14, overflow: 'hidden',
                 backgroundColor: C.surface, borderWidth: 1, borderColor: C.border },
  teamChatEmpty: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 14, padding: 24 },
  teamChatEmptyText: { color: C.textMute, fontSize: 13, textAlign: 'center', lineHeight: 19 },
  teamChatClose: { borderWidth: 1, borderColor: C.cyan, borderRadius: 8, paddingHorizontal: 16, paddingVertical: 8 },
  teamChatCloseText: { color: C.cyan, fontSize: 13, fontWeight: '700' },
  mobilePad: { paddingHorizontal: 14, paddingTop: 12 },
  threatRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  // Épinglé en haut à DROITE du bandeau, au-dessus de l'image (zIndex) et hors du flux du titre.
  muteBtn: { position: 'absolute', top: 10, right: 12, zIndex: 5, padding: 8, borderRadius: 999,
             borderWidth: 1, borderColor: C.border, backgroundColor: 'rgba(10,14,26,0.65)' } as any,
  mobileTabBar: { flexDirection: 'row', backgroundColor: C.surface, borderBottomWidth: 1, borderBottomColor: C.border },
  mobileTabItem: { flex: 1, paddingVertical: 10, alignItems: 'center' },
  mobileTabItemActive: { borderBottomWidth: 2, borderBottomColor: C.cyan },
  mobileTabText: { fontSize: 12, fontWeight: '600', color: C.textMute, fontFamily: 'monospace' },
  mobileTabTextActive: { color: C.cyan },
  headerWrap: { height: 180, position: 'relative', overflow: 'hidden' },
  headerWrapCompact: { height: 96 },
  bannerImg: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' } as any,
  bannerOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(10,14,26,0.78)' },
  bannerContent: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, padding: 20, justifyContent: 'flex-end' },
  backBtn: { position: 'absolute', top: 16, left: 16, width: 36, height: 36, borderRadius: 18, backgroundColor: 'rgba(0,0,0,0.5)', borderWidth: 1, borderColor: C.border, alignItems: 'center', justifyContent: 'center' },
  bannerTitles: { gap: 4 },
  threatBadge: { alignSelf: 'flex-start', paddingHorizontal: 8, paddingVertical: 3, backgroundColor: 'rgba(255,45,85,0.15)', borderWidth: 1, borderColor: C.red, borderRadius: 4 },
  threatText: { color: C.red, fontSize: 10, fontWeight: '700', fontFamily: 'monospace', letterSpacing: 1 },
  missionTitle: { color: '#ffffff', fontSize: 24, fontWeight: '900', fontFamily: 'monospace', letterSpacing: 2, textShadowColor: C.cyan, textShadowOffset: { width: 0, height: 0 }, textShadowRadius: 12 },
  missionSub: { color: C.textSec, fontSize: 12 },
  certRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 4 },
  certBadge: { paddingHorizontal: 8, paddingVertical: 3, backgroundColor: 'rgba(0,240,255,0.12)', borderWidth: 1, borderColor: C.borderCy, borderRadius: 4 },
  certText: { color: C.cyan, fontSize: 10, fontWeight: '700', fontFamily: 'monospace' },
  stepCount: { color: C.textSec, fontSize: 11 },
  timelineWrap: { position: 'relative', paddingLeft: 44 },
  verticalLine: { position: 'absolute', left: 14, top: 12, bottom: 12, width: 2, backgroundColor: 'rgba(0,240,255,0.12)' },
  timelineRow: { flexDirection: 'row', alignItems: 'flex-start', marginBottom: 14 },
  timelineLeft: { width: 44, position: 'absolute', left: -44, top: 14, alignItems: 'center', gap: 4 },
  timelineNum: { fontSize: 9, fontWeight: '800', color: C.textMute, fontFamily: 'monospace' },
  timelineDot: { width: 10, height: 10, borderRadius: 5, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.8, shadowRadius: 6, elevation: 3 },
  cardSelected: { borderColor: C.cyan, backgroundColor: 'rgba(0,240,255,0.06)' },
  card: { flex: 1, backgroundColor: C.card, borderRadius: 12, borderWidth: 1, borderColor: C.border, padding: 14, shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.3, shadowRadius: 8, elevation: 2, overflow: 'hidden' },
  cardLocked: { opacity: 0.55 },
  cardTopRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' },
  typeBadge: { paddingHorizontal: 7, paddingVertical: 3, borderRadius: 5 },
  typeBadgeText: { fontSize: 9, fontWeight: '800', fontFamily: 'monospace', letterSpacing: 0.5 },
  durationRow: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  durationText: { fontSize: 10, color: C.textMute },
  statusBadge: { marginLeft: 'auto' as any, flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 7, paddingVertical: 3, backgroundColor: 'rgba(0,240,255,0.08)', borderRadius: 5 },
  statusLocked: { backgroundColor: 'rgba(255,255,255,0.04)' },
  statusText: { fontSize: 9, fontWeight: '700', fontFamily: 'monospace' },
  cardTitle: { fontSize: 14, fontWeight: '700', color: C.textPrim, marginBottom: 5, lineHeight: 20 },
  cardDesc: { fontSize: 12, color: C.textSec, lineHeight: 17, marginBottom: 8 },
  cardMeta: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 },
  codeChip: { paddingHorizontal: 6, paddingVertical: 3, backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 4 },
  codeText: { fontSize: 10, fontWeight: '600', color: C.textSec, fontFamily: 'monospace' },
  priorityChip: { paddingHorizontal: 6, paddingVertical: 3, borderRadius: 4 },
  priorityText: { fontSize: 10, fontWeight: '700' },
  avatarsRight: { marginLeft: 'auto' as any },
  charAvatarSm: { width: 30, height: 30, borderRadius: 15, marginLeft: 'auto' as any },
  lockOverlay: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 12, alignItems: 'center', justifyContent: 'center', gap: 6, padding: 16 },
  lockText: { color: C.textMute, fontSize: 11, textAlign: 'center' },
  actionBtn: { flexDirection: 'row', alignItems: 'center', gap: 6, alignSelf: 'flex-start', paddingHorizontal: 14, paddingVertical: 7, borderWidth: 1, borderRadius: 7 },
  actionBtnText: { fontSize: 12, fontWeight: '600' },
  panelWrap: { padding: 16, borderBottomWidth: 1, borderBottomColor: C.border },
  panelTitle: { fontSize: 10, fontWeight: '800', color: C.cyan, fontFamily: 'monospace', letterSpacing: 2, marginBottom: 12 },
  progSceneLabel: { fontSize: 12, color: C.textSec, marginBottom: 8 },
  progTrack: { height: 4, backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden', marginBottom: 4 },
  progFill: { position: 'absolute', left: 0, top: 0, height: '100%', backgroundColor: C.cyan, borderRadius: 2 },
  progItem: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  progDot: { width: 7, height: 7, borderRadius: 4, flexShrink: 0 },
  progItemText: { fontSize: 11, flex: 1 },
  teamRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 12 },
  teamAvatar: { width: 36, height: 36, borderRadius: 18, flexShrink: 0 },
  teamAvatarMe: { borderWidth: 2, borderColor: C.cyan } as any,
  teamAvatarFb: { backgroundColor: 'rgba(0,240,255,0.12)', alignItems: 'center', justifyContent: 'center' },
  teamAvatarInit: { color: C.cyan, fontSize: 13, fontWeight: '700' },
  teamInfo: { flex: 1 },
  teamName: { fontSize: 12, fontWeight: '700', color: C.textPrim },
  teamRole: { fontSize: 10, color: C.textSec, marginBottom: 4 },
  relationRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  relationTrack: { flex: 1, height: 3, backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' },
  relationFill: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 2 },
  relationScore: { fontSize: 10, fontFamily: 'monospace', flexShrink: 0 },
  chatIconBtn: { padding: 6, marginLeft: 4 },
  rewardRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 10 },
  rewardLabel: { flex: 1, fontSize: 12, color: C.textSec },
  rewardValue: { fontSize: 12, fontWeight: '700', fontFamily: 'monospace' },
  objRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 10 },
  objIcon: { width: 18, height: 18, borderRadius: 4, borderWidth: 1, alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 },
  objText: { fontSize: 12, flex: 1, lineHeight: 17 },
  objDone: { textDecorationLine: 'line-through' },
  abandonBtn: { margin: 16, paddingVertical: 12, paddingHorizontal: 16, borderWidth: 1, borderColor: C.red, borderRadius: 8, backgroundColor: 'rgba(255,45,85,0.08)', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  abandonText: { color: C.red, fontSize: 12, fontWeight: '700', fontFamily: 'monospace', letterSpacing: 1 },
  modalBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.8)', alignItems: 'center', justifyContent: 'center' },
  // CCT-346 — larger than the team-chat modal: these three are full screens (a board of six
  // columns, a notification centre). Cramped, one would spend the visit scrolling.
  // ⚠️ NO CLOSE-ON-BACKDROP HERE, unlike the team chat: the board carries a drag-and-drop and the
  // notification centre a list — a gesture ending outside the box would close the panel mid-move.
  panelBackdrop: { flex: 1, backgroundColor: 'rgba(0,0,0,0.8)', alignItems: 'center', justifyContent: 'center' },
  panelBox: { width: '94%', maxWidth: 1200, height: '90%', borderRadius: 14, overflow: 'hidden',
              backgroundColor: C.bg, borderWidth: 1, borderColor: C.border },
  // CCT-346 — dropdown of the notification centre, hanging under its own bell (large screen).
  // ⚠️ THE MODAL IS A PORTAL: these coordinates are the VIEWPORT's, not the mission screen's, so
  // `top` clears the 52 px application header and `right` lines the panel up with the bell and
  // the cart button that sit at that end of it.
  panelDropdownBackdrop: { flex: 1, backgroundColor: 'transparent', alignItems: 'flex-end' },
  panelDropdown: { marginTop: 58, marginRight: 12, width: 400, maxWidth: '96%' as any,
                   maxHeight: 560, borderRadius: 14, overflow: 'hidden', backgroundColor: C.bg,
                   borderWidth: 1, borderColor: C.border,
                   // Detached from the header: without a shadow the panel and the page merge into
                   // one another on the dark theme.
                   shadowColor: '#000', shadowOffset: { width: 0, height: 8 },
                   shadowOpacity: 0.45, shadowRadius: 18, elevation: 12 },
  panelClose: { position: 'absolute', top: 10, right: 12, zIndex: 10, padding: 8, borderRadius: 999,
                borderWidth: 1, borderColor: C.border, backgroundColor: C.surface },
  modalCard: { borderRadius: 14, padding: 22, width: '100%', maxWidth: 420, marginHorizontal: 24 },
  modalBox: { width: 420, maxWidth: '90%' as any, maxHeight: '80%' as any, backgroundColor: C.surface, borderWidth: 1, borderColor: C.borderCy, borderRadius: 12, overflow: 'hidden' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 14, borderBottomWidth: 1, borderBottomColor: C.border },
  modalCharInfo: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  modalAvatar: { width: 40, height: 40, borderRadius: 20 },
  modalAvatarFb: { backgroundColor: 'rgba(0,240,255,0.12)', alignItems: 'center', justifyContent: 'center' },
  modalAvatarInit: { color: C.cyan, fontSize: 16, fontWeight: '700' },
  modalCharName: { fontSize: 14, fontWeight: '700', color: C.textPrim },
  modalCharRole: { fontSize: 11, color: C.textSec },
  modalCloseBtn: { padding: 6 },
  messageList: { maxHeight: 300, padding: 12 },
  emptyChat: { color: C.textMute, fontSize: 12, textAlign: 'center', paddingVertical: 20 },
  messageBubble: { maxWidth: '75%' as any, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 12, marginBottom: 8 },
  bubblePlayer: { alignSelf: 'flex-end', backgroundColor: 'rgba(0,240,255,0.15)', borderWidth: 1, borderColor: C.borderCy },
  bubbleChar: { alignSelf: 'flex-start', backgroundColor: 'rgba(255,255,255,0.06)', borderWidth: 1, borderColor: C.border },
  bubbleText: { fontSize: 13, lineHeight: 18 },
  bubbleTextPlayer: { color: C.cyan },
  bubbleTextChar: { color: C.textSec },
  chatInputRow: { flexDirection: 'row', alignItems: 'center', gap: 8, padding: 12, borderTopWidth: 1, borderTopColor: C.border },
  chatInput: { flex: 1, height: 38, paddingHorizontal: 12, backgroundColor: C.card, borderWidth: 1, borderColor: C.border, borderRadius: 8, color: C.textPrim, fontSize: 13 },
  sendBtn: { width: 38, height: 38, borderRadius: 8, backgroundColor: 'rgba(0,240,255,0.1)', borderWidth: 1, borderColor: C.borderCy, alignItems: 'center', justifyContent: 'center' },
  // Chapter accordion styles
  chapterAccordion: { marginBottom: 4 },
  chapterHeader: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 8, paddingHorizontal: 4, borderRadius: 6 },
  chapterHeaderLocked: { opacity: 0.5 },
  chapterNum: { width: 22, height: 22, borderRadius: 5, alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  chapterNumText: { fontSize: 9, fontWeight: '800', fontFamily: 'monospace' },
  chapterTitle: { fontSize: 11, fontWeight: '700', color: C.textPrim, marginBottom: 3 },
  chapterProgRow: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  chapterProgTrack: { flex: 1, height: 2, backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: 1, overflow: 'hidden' },
  chapterProgFill: { position: 'absolute', left: 0, top: 0, height: '100%', borderRadius: 1 },
  chapterProgPct: { fontSize: 9, fontFamily: 'monospace', flexShrink: 0 },
  chapterItems: { paddingLeft: 30, paddingBottom: 4, gap: 2 },
});
